// some urls can include a "tag" in the URL
// e.g. https://help.lifeworks.com/hc/en-gb/articles/209973963
// redirects to
// https://help.lifeworks.com/hc/en-gb/articles/209973963-Privacy-Policy
// The URLs given here do not include that tag text because it is subject to change
// and is added automatically
export const helpPaths = {
  admin_panel: 'hc/en-gb/categories/201196576',
  accessibility_tips: 'hc/en-gb/articles/360043832591',
  cashback_faq: 'hc/en-gb/sections/201749356',
  challenges_info: 'hc/en-gb/categories/360000075883',
  custom_tiering_info: 'hc/en-gb/sections/360008763431',
  delete_account: 'hc/en-gb/requests/new?ticket_form_id=360000011846',
  end_user_license: 'hc/en-gb/articles/209973913',
  help_centre: 'hc/en-gb',
  getting_started: 'hc/en-gb/articles/208461636',
  multifactor_authentication: 'hc/en-gb/articles/360059052312',
  offers_faq: 'hc/en-gb/requests/new?ticket_form_id=227483',
  perks_need_help: 'hc/en-gb/sections/201739433',
  privacy_policy: 'hc/en-gb/articles/209973963',
  tiering_info: 'hc/en-gb/articles/360000906966',
  user_terms: 'hc/en-gb/categories/201166406',
  wallet: 'hc/en-gb/sections/201749386',
} as const;

type HelpPathKeys = keyof typeof helpPaths;

export const privacyPaths: ReadonlyArray<HelpPathKeys> = [
  'delete_account',
] as const;

// these articles are expected to be present in all languages
// other paths are expected to be present in core languages only
export const allLanguagesPaths: ReadonlyArray<HelpPathKeys> = [
  // 'accessibility_tips', // this is only in Core languages but it should be available in all languages so we hope to restore it once translations are available
  'privacy_policy',
  'user_terms',
] as const;

// there is a ZenDesk sandbox available at https://lifeworks1689151973.zendesk.com
// but it is not configured for login and does not have the site content
// by using the live Help Centre everywhere, the public links will work in Test / Dev / Integration
export const helpCentreRootUrls = {
  live: 'https://help.one.telushealth.com',
  test: 'https://help.one.telushealth.com',
  development: 'https://help.one.telushealth.com',
  integration: 'https://help.one.telushealth.com',
};

export const privacyCenterRootUrls = {
  live: 'https://privacy.lifeworks.com',
  test: 'https://privacy.lifeworks.com',
  development: 'https://privacy.lifeworks.com',
  integration: 'https://privacy.lifeworks.com',
};

export const helpCentreLoginRootUrls = {
  live: 'https://lifeworks.zendesk.com',
  test: 'https://lifeworks.zendesk.com',
  development: 'https://lifeworks.zendesk.com',
  integration: 'https://lifeworks.zendesk.com',
};

export const helpCentreLoginPath = 'access';

export const defaultEnvironment = 'live';

export const defaultLocale = 'en_GB';

// map our supported locales to the Help Centre (Zen Desk) locale codes
export const helpCentreLocaleCodes = {
  en_GB: 'en-gb', // English (GB)
  en_US: 'en-us', // English (US)
  en_CA: 'en-ca', // English (Canada)
  en_AU: 'en-au', // English (Australia)
  es_US: 'es-419', // Español (US)
  fr_CA: 'fr-ca', // Français (Canada)
  af_ZA: 'af', // Afrikaans
  am_ET: 'am', // አማርኛ
  ar_001: 'ar', // العربية
  az_AZ: 'az', // Azərbaycan
  bn_BD: 'bn', // বাংলা
  bs_BA: 'bs', // Bosanski
  bg_BG: 'bg', // български
  zh_CN: 'zh-cn', // 简体中文 (simplified Chinese)
  zh_HK: 'zh-hk', // 繁体中文 (traditional Chinese)
  hr_HR: 'hr', // hrvatski
  cs_CZ: 'cs', // Česká
  da_DK: 'da', // Dansk
  nl_NL: 'nl', // Nederlands
  et_EE: 'et', // Eesti
  fi_FI: 'fi', // Suomi
  fr_FR: 'fr-fr', // Français
  de_DE: 'de', // Deutsch
  el_GR: 'el', // Ελληνικά
  he_IL: 'he', // עברית
  hi_IN: 'hi', // हिंदी
  hu_HU: 'hu', // Magyar
  id_ID: 'id-id', // Bahasa Indonesia
  it_IT: 'it', // Italiano
  ja_JP: 'ja', // 日本語
  ko_KR: 'ko', // 한국어
  lv_LV: 'lv', // Latviski
  lt_LT: 'lt', // Lietuvių
  ms_MY: 'ms', // Bahasa Malaysia
  mn_MN: 'mn', // монгол
  ne_NP: 'ne', // नेपाली
  nb_NO: 'no', // Norsk
  pl_PL: 'pl', // Polski
  pt_BR: 'pt-br', // Português (Brasil)
  pt_PT: 'pt', // Português (Portugal)
  ro_RO: 'ro', // Română
  ru_RU: 'ru', // Русский
  sr_RS: 'sr', // српски
  sk_SK: 'sk', // Slovenský
  sl_SI: 'sl', // Slovenščina
  so_SO: 'so', // Soomaali
  es_ES: 'es', // Español
  es_419: 'es-419', // Español (América Latina)
  sw_KE: 'sw', // Kiswahili
  sv_SE: 'sv', // Svenska
  tl_PH: 'tl', // Tagalog
  ta_IN: 'ta-in', // தமிழ்
  th_TH: 'th', // ไทย
  tr_TR: 'tr', // Türkçe
  uk_UA: 'uk', // Українська
  ur_PK: 'ur', // اردو
  vi_VN: 'vi', // Tiếng Việt
  xh_ZA: 'xh', // IsiXhosa
  zu_ZA: 'zu-za', // isiZulu
} as const;

export type HelpCentreLocaleKeys = keyof typeof helpCentreLocaleCodes;

export const coreCountryLocales: ReadonlyArray<HelpCentreLocaleKeys> = [
  'en_GB',
  'en_US',
  'en_CA',
  'en_AU',
  'fr_CA',
];
